import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';

interface HelpHintProps {
  /** Hjelpetekst som vises når ikonet trykkes. */
  text: string;
  /** Tilgjengelig etikett for trykk-knappen (skjermleser). */
  label?: string;
  className?: string;
}

/**
 * Tablet-vennlig hjelpe-ikon: et lite (?) som ved TRYKK (ikke hover) viser/skjuler en kort
 * hjelpetekst. Hover-tooltips finnes ikke på touch, og bittesmå trykkmål er vanskelige å treffe —
 * derfor er trykkmålet >= 44px selv om ikonet er visuelt lite. Lukkes ved nytt trykk eller ved
 * klikk/trykk utenfor.
 */
export const HelpHint: React.FC<HelpHintProps> = ({ text, label = 'Hjelp', className = '' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointer);
    return () => document.removeEventListener('pointerdown', onDocPointer);
  }, [open]);

  return (
    <span ref={ref} className={`relative inline-flex items-center align-middle ${className}`}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] -my-3 -mx-2 text-gray-400 hover:text-gray-600 active:text-gray-700"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute z-30 top-full left-0 mt-1 w-56 max-w-[80vw] rounded-lg border border-gray-200 bg-white p-2 text-xs leading-snug text-gray-600 shadow-lg"
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default HelpHint;
